import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { PermissionProvider, usePermissions } from "./context/PermissionContext";
import Login from "./components/Login";
import Register from "./components/Register";
//import Dashboard from './components/Dashboard';
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import User from "./components/User";
import Role from "./components/Role";
import RolePermissions from "./components/RolePermissions";

import AddRole from "./components/AddRole";
import AddBlog from "./components/AddBlog";
import Blogs from "./components/Blogs";
import EditBlog from "./components/EditBlog";
import ViewBlogPost from "./components/ViewBlogPost";
import PublicViewBlog from "./components/PublicViewBlog";
import Galleries from "./components/Galleries";
import AddGallery from "./components/AddGallery";
import EditGallery from "./components/EditGallery";
import GalleryManagement from "./components/GalleryManagement";
import AppDownloadSettings from "./components/AppDownloadSettings";
import ContactMessages from "./components/ContactMessages";

import EditUser from "./components/EditUser";
import Home from "./components/Home";
import AddBookmark from "./components/AddBookmark.js";
import BookmarkList from "./components/BookmarkList.js";
import EditBookmark from "./components/EditBookmark.js";
import BookmarkProfile from "./components/BookmarkProfile.js";
import TrackerDashboard from "./components/TrackerDashboard.js";
import TrackerDetails from "./components/TrackerDetails.js";
import HeroImageManagement from "./components/HeroImageManagement.js";
import ApprovalManagement from "./components/ApprovalManagement.js";
import ApprovalDetail from "./components/ApprovalDetail.js";
import EmployeeList from "./components/EmployeeList.js";
import EmployeeForm from "./components/EmployeeForm.js";
import EmploymentLetter from "./components/EmploymentLetter.js";
import EmploymentLetterTemplate from "./components/EmploymentLetterTemplate.js";
import { AuthProvider, useAuth } from "./context/auth";

// Landing page
import LandingPage from "./components/LandingPage";

const App = () => {
  return (
    <AuthProvider>
      <PermissionProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/blog/:id" element={<PublicViewBlog />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Protected Dashboard Routes */}
            <Route path="/dashboard/*" element={<ProtectedRoute />} />
          </Routes>
        </Router>
      </PermissionProvider>
    </AuthProvider>
  );
};

// ProtectedRoute Component - Only renders dashboard layout for authenticated users
const ProtectedRoute = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <DashboardLayout />;
};

// OperatorProtectedBookmark - Ensures operators can only access their assigned bookmark
const OperatorProtectedBookmark = ({ bookmarkId }) => {
  const { id } = useParams();

  // If operator tries to access a different bookmark, redirect to their assigned one
  if (id !== bookmarkId) {
    return <Navigate to={`/dashboard/bookmarkProfiles/${bookmarkId}`} replace />;
  }

  return <BookmarkProfile />;
};

// Dashboard layout with Sidebar and Topbar only for authenticated users
const DashboardLayout = () => {
  const { hasPermission } = usePermissions();
  const bookmarkId = localStorage.getItem('bookmarkId');
  const role = localStorage.getItem('role');

  // Check if user has admin-level access (user view permission)
  const hasAdminAccess = hasPermission('user', 'view');

  // Check if user is an Operator (hide sidebar for Operators only)
  const isOperator = role === 'Operator';

  return (
    <div style={{ display: "flex" }}>
      {/* Hide sidebar for Operator users only */}
      {!isOperator && <Sidebar />}
      <div style={{ flex: 1, width: !hasAdminAccess && bookmarkId ? '100%' : 'auto' }}>
        <Topbar />
        <Routes>
          {/* Limited-access user routes (with bookmark assignment) */}
          {!hasAdminAccess && bookmarkId && (
            <>
              <Route path="bookmarkProfiles/:id" element={<OperatorProtectedBookmark bookmarkId={bookmarkId} />} />
              <Route path="*" element={<Navigate to={`/dashboard/bookmarkProfiles/${bookmarkId}`} replace />} />
            </>
          )}

          {/* Routes for admin-level users or users without bookmark assignment */}
          {(hasAdminAccess || !bookmarkId) && (
            <>
              <Route index element={<Home />} />
              <Route path="home" element={<Home />} />
              <Route path="users" element={<User />} />
              <Route path="roles" element={<Role />} />
              <Route path="role-permissions" element={<RolePermissions />} />
              <Route path="register" element={<Register />} />
              <Route path="addRole" element={<AddRole />} />
              <Route path="editUsers/:id" element={<EditUser />} />
              <Route path="employees" element={<EmployeeList />} />
              <Route path="employees/add" element={<EmployeeForm />} />
              <Route path="employees/edit/:id" element={<EmployeeForm />} />
              <Route path="employees/:id/letter" element={<EmploymentLetter />} />
              <Route path="employment-letter-template" element={<EmploymentLetterTemplate />} />
              <Route path="addBookmark" element={<AddBookmark />} />
              <Route path="bookmarks" element={<BookmarkList />} />
              <Route path="approvals" element={<ApprovalManagement />} />
              <Route path="approvals/:id" element={<ApprovalDetail />} />
              <Route path="editBookmark/:id" element={<EditBookmark />} />
              <Route path="bookmarkProfiles/:id" element={<BookmarkProfile />} />
              <Route path="trackers" element={<TrackerDashboard />} />
              <Route path="trackers/:id" element={<TrackerDetails />} />
              <Route path="hero-images" element={<HeroImageManagement />} />
              <Route path="addBlog" element={<AddBlog />} />
              <Route path="blogs" element={<Blogs />} />
              <Route path="editBlog/:id" element={<EditBlog />} />
              <Route path="/view/blog/:id" element={<ViewBlogPost />} />
              <Route path="galleries" element={<Galleries />} />
              <Route path="galleryManagement" element={<GalleryManagement />} />
              <Route path="addGallery" element={<AddGallery />} />
              <Route path="editGallery/:id" element={<EditGallery />} />
              <Route path="app-download-settings" element={<AppDownloadSettings />} />
              <Route path="contact/messages" element={<ContactMessages />} />
            </>
          )}
        </Routes>
      </div>
    </div>
  );
};

export default App;
